import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, API_BASE, getBearer } from "../../lib/api";
import { extractError } from "../../lib/format";
import { Loader2, X, ShieldCheck, AlertTriangle, RotateCcw } from "lucide-react";

const GRADES = ["all", "excellent", "good", "attempted"];
const REVIEWS = ["all", "unreviewed", "reviewed", "flagged"];
const BANDS = ["all", "age_6-7", "age_8-9", "age_10-12", "age_13-14", "age_15-17"];

function bandLabel(id) {
  if (!id || id === "all") return "All bands";
  return "Ages " + id.replace("age_", "").replace("-", "\u2013");
}
function formatWhen(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}
function truncate(s, n = 140) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n) + "…" : s;
}

function SubmissionDrawer({ subId, onClose, onReviewed }) {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("reviewed");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/submissions/${subId}`)
      .then(({ data }) => {
        if (!alive) return;
        setSub(data.submission);
        setStatus(data.submission.review_status || "reviewed");
        setNotes(data.submission.admin_notes || "");
      })
      .catch((e) => toast.error(extractError(e, "Could not load submission.")))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [subId]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/submissions/${subId}/review`, {
        review_status: status, admin_notes: notes,
      });
      toast.success("Saved.");
      onReviewed && onReviewed(data.submission);
      onClose();
    } catch (e) {
      toast.error(extractError(e, "Could not save."));
    } finally {
      setSaving(false);
    }
  };

  const imgSrc = (n) => {
    const t = getBearer();
    return `${API_BASE}/api/submissions/${subId}/image/${n}?token=${encodeURIComponent(t)}`;
  };

  return (
    <div className="ftg-adm__drawer" role="dialog" aria-modal="true" data-testid="sub-drawer">
      <div className="ftg-adm__drawer-scrim" onClick={onClose} />
      <aside className="ftg-adm__drawer-panel">
        <header className="ftg-adm__drawer-head">
          <h3 className="ftg-adm__drawer-title">Submission details</h3>
          <button type="button" onClick={onClose} className="ftg-adm__drawer-close" data-testid="sub-drawer-close" aria-label="Close">
            <X size={16} />
          </button>
        </header>
        <div className="ftg-adm__drawer-body">
          {loading && <div className="ftg-adm__empty"><Loader2 className="ftg-spin" size={16} /> Loading…</div>}
          {sub && (
            <>
              <dl className="ftg-adm__kv">
                <div><dt>Lesson</dt><dd>{sub.lesson_title}</dd></div>
                <div><dt>Age band</dt><dd>{bandLabel(sub.age_band)}</dd></div>
                <div><dt>Submitted</dt><dd>{formatWhen(sub.submitted_at)}</dd></div>
                <div><dt>AI grade</dt><dd><span className={`ftg-adm__pill is-${sub.ai_grade}`}>{sub.ai_grade || "—"}</span></dd></div>
              </dl>

              <h4 className="ftg-adm__drawer-h4">Pages ({sub.image_count})</h4>
              <div className="ftg-adm__thumbs" data-testid="sub-thumbs">
                {Array.from({ length: sub.image_count }).map((_, i) => (
                  <a key={i} href={imgSrc(i + 1)} target="_blank" rel="noopener noreferrer" className="ftg-adm__thumb">
                    <img src={imgSrc(i + 1)} alt={`Page ${i + 1}`} />
                  </a>
                ))}
              </div>

              <h4 className="ftg-adm__drawer-h4">AI transcription</h4>
              <div className="ftg-adm__quote" data-testid="sub-transcription">
                {sub.ocr_text || <span className="ftg-adm__muted">(empty)</span>}
              </div>
              <h4 className="ftg-adm__drawer-h4">AI feedback</h4>
              <div className="ftg-adm__quote" data-testid="sub-ai-feedback">{sub.ai_feedback}</div>

              <h4 className="ftg-adm__drawer-h4">Review action</h4>
              <div className="ftg-adm__review-actions" data-testid="sub-review-actions">
                <button
                  type="button"
                  className={`ftg-btn ftg-btn--sm ${status === "reviewed" ? "ftg-btn--primary" : "ftg-btn--ghost"}`}
                  onClick={() => setStatus("reviewed")}
                  data-testid="review-set-reviewed"
                >
                  <ShieldCheck size={14} /> Mark reviewed
                </button>
                <button
                  type="button"
                  className={`ftg-btn ftg-btn--sm ${status === "flagged" ? "ftg-btn--secondary" : "ftg-btn--ghost"}`}
                  onClick={() => setStatus("flagged")}
                  data-testid="review-set-flagged"
                >
                  <AlertTriangle size={14} /> Flag
                </button>
                <button
                  type="button"
                  className={`ftg-btn ftg-btn--sm ${status === "unreviewed" ? "ftg-btn--primary" : "ftg-btn--ghost"}`}
                  onClick={() => setStatus("unreviewed")}
                  data-testid="review-set-unreviewed"
                >
                  <RotateCcw size={14} /> Reset
                </button>
              </div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional note to the parent (shown on the parent's Dashboard)"
                className="ftg-adm__textarea"
                rows={3}
                data-testid="review-notes-textarea"
              />
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="ftg-btn ftg-btn--primary"
                data-testid="review-save-btn"
              >
                {saving ? "Saving…" : "Save review"}
              </button>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

export default function AdminSubmissions() {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [band, setBand] = useState("all");
  const [grade, setGrade] = useState("all");
  const [review, setReview] = useState("all");
  const [drawerId, setDrawerId] = useState(null);
  const [page, setPage] = useState(0);
  const LIMIT = 50;

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get(`/admin/submissions?skip=${page * LIMIT}&limit=${LIMIT}`);
      setState({ loading: false, data, error: null });
    } catch (e) {
      setState({ loading: false, data: null, error: extractError(e, "Could not load submissions.") });
    }
  };
  useEffect(() => { load(); }, [page]);

  const filtered = useMemo(() => {
    const items = state.data?.items || [];
    return items.filter((s) =>
      (band === "all" || s.age_band === band) &&
      (grade === "all" || s.ai_grade === grade) &&
      (review === "all" || (s.review_status || "unreviewed") === review)
    );
  }, [state.data, band, grade, review]);

  return (
    <div className="ftg-adm__stack" data-testid="subs-root">
      <div className="ftg-adm__toolbar">
        <label className="ftg-adm__filter" data-testid="filter-band">
          <span>Band</span>
          <select value={band} onChange={(e) => setBand(e.target.value)}>
            {BANDS.map((b) => <option key={b} value={b}>{bandLabel(b)}</option>)}
          </select>
        </label>
        <label className="ftg-adm__filter" data-testid="filter-grade">
          <span>Grade</span>
          <select value={grade} onChange={(e) => setGrade(e.target.value)}>
            {GRADES.map((g) => <option key={g} value={g}>{g === "all" ? "All grades" : g}</option>)}
          </select>
        </label>
        <label className="ftg-adm__filter" data-testid="filter-review">
          <span>Review</span>
          <select value={review} onChange={(e) => setReview(e.target.value)}>
            {REVIEWS.map((r) => <option key={r} value={r}>{r === "all" ? "All reviews" : r}</option>)}
          </select>
        </label>
      </div>

      {state.loading && <div className="ftg-adm__empty" data-testid="subs-loading"><Loader2 className="ftg-spin" size={16} /> Loading…</div>}
      {state.error && <div className="ftg-adm__empty" data-testid="subs-error">{state.error}</div>}
      {state.data && (
        <div className="ftg-adm__table-wrap">
          <table className="ftg-adm__table" data-testid="subs-table">
            <thead>
              <tr>
                <th>Submitted</th><th>Lesson</th><th>Band</th><th>Grade</th>
                <th>Feedback preview</th><th>Review</th><th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id} data-testid={`sub-row-${s.id}`}>
                  <td className="ftg-adm__nowrap">{formatWhen(s.submitted_at)}</td>
                  <td>{s.lesson_title}</td>
                  <td>{bandLabel(s.age_band)}</td>
                  <td><span className={`ftg-adm__pill is-${s.ai_grade}`}>{s.ai_grade || "—"}</span></td>
                  <td className="ftg-adm__muted">{truncate(s.ai_feedback)}</td>
                  <td>
                    <span className={`ftg-adm__pill is-review-${s.review_status || "unreviewed"}`}>
                      {s.review_status || "unreviewed"}
                    </span>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="ftg-btn ftg-btn--ghost ftg-btn--sm"
                      onClick={() => setDrawerId(s.id)}
                      data-testid={`sub-open-${s.id}`}
                    >
                      Review
                    </button>
                  </td>
                </tr>
              ))}
              {!filtered.length && <tr><td colSpan={7} className="ftg-adm__muted">No submissions match the filters.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {state.data && (
        <div className="ftg-adm__pager">
          <button type="button" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="ftg-btn ftg-btn--ghost ftg-btn--sm">Prev</button>
          <span className="ftg-adm__pager-info">
            {state.data.total ? `${page * LIMIT + 1}–${Math.min(state.data.total, (page + 1) * LIMIT)} of ${state.data.total}` : "0 of 0"}
          </span>
          <button
            type="button"
            disabled={(page + 1) * LIMIT >= (state.data.total || 0)}
            onClick={() => setPage((p) => p + 1)}
            className="ftg-btn ftg-btn--ghost ftg-btn--sm"
          >
            Next
          </button>
        </div>
      )}
      {drawerId && (
        <SubmissionDrawer subId={drawerId} onClose={() => setDrawerId(null)} onReviewed={() => load()} />
      )}
    </div>
  );
}
